import React, { useState, useEffect, useRef } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { ButtonGroup } from 'components/ui/button'
import { Fieldset, FieldSection } from 'components/ui/fieldset'
import { FooterEditEntity } from 'components/ui/footer'
import { IconCancel, IconTick } from "components/ui/icons";
import { Input } from 'components/ui/input'
import { RadioInputElement } from 'components/ui/radio'
import { Description, Image, Title, ListItemLabel, Subtitle } from 'components/ui/list-item-label'

import { MediaField } from 'domains/media/components/media-field'
import { MobileMockup } from 'components/ui/mobile-mockup'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { moodSchema } from 'domains/org/getty/schemas/mood'
import { GettyStore } from 'domains/org/getty/store'
import { getValidationWarning } from 'domains/org/getty/utils/validation'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { Page } from 'generated/mos/pagination'
import { GetMoodRequest } from 'generated/mos/org/getty/audioaccess'
import { Media } from 'generated/mos/media'
import { MoodColor, MoodColorStyle } from 'generated/mos/org/getty/color'
import { Mood } from 'generated/mos/org/getty/audio'
import { UpdateMoodRequest } from 'generated/mos/org/getty/audiomanagement'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { convertEntityToFormValues, writeUsingSchema } from 'helpers/schema'
import { baseDeepLinkDomain, gettyCenterSiteId, gettyVillaSiteId } from 'helpers/deep-links'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { getMoodSubnav } from '../mood-update/subnav'
import { MoodDelete } from '../mood-delete'

type FormValues = {
  name: string | undefined;
  description?: string | undefined;
  imageAltText: string | undefined;
  creditText: string | undefined;
  color: string | undefined;
  key: string | undefined;
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetMoodRequest' | 'gettyUpdateMoodRequest' | 'gettyUpdateMoodReset' | 'gettyListValidationFailuresRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'moodUpdate' | 'validationFailuresList'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly moodId: string;
}

type Props = ActionProps & ConnectedProps & DirectProps

const statusSummary = (failures: {issues: ReadonlyArray<ValidationFailure.Issue.Entity>} | undefined) => {
  const warning: Array<{text: string}> = [];
  const failureFields = failures && failures.issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'MISSING_REQUIRED_ATTRIBUTE')
  if (failureFields && !!failureFields.length) {
    warning.push({ text: `The following fields are required to publish this mood journey: ${failureFields.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.` });
  }
  if (failures && failures.issues.find(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'INVALID_COLOR')) {
    warning.push({ text: "Mood journey contains invalid colors" });
  }

  if (warning.length) {
    return function renderStatusSummary() {
      return <StatusSummary warning={warning} />
    }
  } else {
    return undefined
  }
}

// calculation values from https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
const calculateColorContrast = (color1: string, color2: string): number => {
  const getRGB = (color: string) => {
    let hex = color.replace('#', '');
    let r = parseInt(hex.substring(0,2), 16) / 255;
    let g = parseInt(hex.substring(2,4), 16) / 255;
    let b = parseInt(hex.substring(4,6), 16) / 255;

    return [r, g, b];
  };

  const calculateLuminance = (color: string) => {
    let rgb = getRGB(color);

    let a = rgb.map((v) => {
      return v <= 0.03928
        ? v / 12.92
        : Math.pow((v + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  };

  let l1 = calculateLuminance(color1) + 0.05;
  let l2 = calculateLuminance(color2) + 0.05;

  return l1 > l2 ? l1 / l2 : l2 / l1;
};

const MoodUpdateDetailsView = (props: Props) => {
  const {
    gettyGetMoodRequest,
    gettyUpdateMoodRequest,
    gettyUpdateMoodReset,
    historyPush,
    projectId,
    toastNotification,
    moodId,
    moodUpdate,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
    gettyListValidationFailuresRequest,
    validationFailuresList,
  } = props

  // set up form values
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ imageMedia, setImageMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ audioMedia, setAudioMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ colorStyle, setColorStyle ] = useState<MoodColorStyle | undefined>(undefined)
  const [ colorHex, setColorHex ] = useState<string | undefined>(undefined)

  const [deleteMoodModalOpen, setDeleteMoodModalOpen] = React.useState(false)

  const backLocation = `/project/${projectId}/moods`

  const moodUpdateData = statusSelector.data(moodUpdate)
  const mood = moodUpdateData && moodUpdateData.mood
  const [siteId, setSiteId] = useState('');
  const validationFailuresListData = statusSelector.data(validationFailuresList)

  const moodRef = { typename: Mood.refName, id: props.moodId }

  const hasSubmitted = useRef(false)

  const colorStyleOption = (key: MoodColorStyle, name: string) => {
    return (
      <ListItemLabel key={key}>
        <RadioInputElement checked={colorStyle == key} name={key} onChange={event => setColorStyle(key)} />
        <Description>
          <Title>{name}</Title>
        </Description>
      </ListItemLabel>
    );
  }

  useEffect(() => {
    // once the entity is ready, initialise the form values, or reinitialise once an update was
    // successful.
    if (mood && initialFormValues && moodUpdate.status === Status.Ready) {
      setFormBag(createFormBag(initialFormValues))
      setImageMedia(initialImageValue)
      setAudioMedia(initialAudioValue)
      setColorStyle(initialColorStyle)
      setColorHex(initialColorHex)
    }

    if (moodUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([{
        typename: Mood.refName,
        id: moodId
      }])
      toastNotification({ type: 'success', text: 'Mood journey updated' })
      hasSubmitted.current = false
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs: [{
            typename: Mood.refName,
            id: moodId,
          }]
        }
      })
    }

  }, [moodUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mood && mood.siteRef && mood.siteRef.id) {
      setSiteId(mood.siteRef.id)
    }
  }, [mood]); 

  // load initial data
  if (moodUpdate.status === Status.Idle || (mood && Mood.mustRef(mood.ref).id !== moodId)) {
    gettyGetMoodRequest({
      type: GetMoodRequest.refName,
      moodRef: {
        typename: Mood.refName,
        id: moodId
      }
    })
    releaseControlsListReleaseControlsRequest([{
      typename: Mood.refName,
      id: moodId
    }])
    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [{
          typename: Mood.refName,
          id: moodId,
        }]
      }
    })
    return null;
  }

  const moodValues: Partial<Mood.Entity> = pick({
    ...Mood.defaults,
    ...mood
  }, 'name', 'description', 'imageAltText', 'creditText', 'color', 'key');

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = mood ?
    convertEntityToFormValues<Mood.Entity>(moodValues, moodSchema) as any as FormValues:
    undefined;
  const initialImageValue = mood && moodSchema.imageRef.read(mood.imageRef) as any as Media.Ref | undefined;
  const initialAudioValue = mood && moodSchema.audio.read(mood.audio) as Media.Ref | undefined;
  const initialColorHex = mood && mood.color && mood.color.hex as string | undefined;
  const initialColorStyle = mood && mood.color && mood.color.style as MoodColorStyle | undefined;


  // some values are handled by the comparison between initialFormValues and
  // formBag.values but also have their own initial value (eg. initialColorHex)
  const modified = initialFormValues && formBag && mood && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialImageValue, imageMedia) ||
    !areRefValuesEqual(initialAudioValue, audioMedia) ||
    colorStyle !== initialColorStyle
  );

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};

    if (!values.name || !values.name.trim().length) {
      errors.name = 'Mood journey name required';
    }
    return errors;
  };

  const submit = (formBag: FormBag<FormValues>, mood: Mood.Entity) => {
    const bag = validateFormBag(formBag, validateForm);
    setFormBag(bag);

    if (!bag.valid) {
      return false;
    }

    const entityValues = Object.entries(formBag.values).reduce((accumulator, currentValue) => {
      const key = currentValue[0] as keyof Mood.Entity;
      const value = currentValue[1];
      return {
        ...accumulator,
        [key]: moodSchema[key] ? moodSchema[key].write(value, mood[key]) : value
      };
    }, {});

    gettyUpdateMoodRequest({
      ...UpdateMoodRequest.defaults,
      moodRef: Mood.mustRef(mood.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'name',
          'description',
          'imageRef',
          'imageAltText',
          'creditText',
          'color',
          'audio',
          'key',
        ]
      },
      ...entityValues,
      imageRef: imageMedia ? moodSchema.imageRef ? moodSchema.imageRef.write(imageMedia) : imageMedia : undefined,
      audio: writeUsingSchema<Mood.Entity>(moodSchema, 'audio', audioMedia, mood.audio),
      color: colorStyle && colorHex ? { ...MoodColor.defaults, style: colorStyle, hex: colorHex } as MoodColor.Entity : undefined
    })

    hasSubmitted.current = true

    return false
  };

  // if the status is stale, trigger failure toast
  if (moodUpdate.status === Status.Stale) {
    gettyUpdateMoodReset()
    toastNotification({ type: 'error', text: 'Can’t save because of an unknown error' })
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    mood && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: moodRef,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.id === moodId) : undefined
  const failureFields = failures && failures.issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'MISSING_REQUIRED_ATTRIBUTE' || issueType === 'TOUR_CONTAINS_STOPS_AND_HAS_WEBPAGE_URL')

  let textColor: string
  let uiColor: string

  switch (colorStyle) {
      case "UNSET":
          textColor = '#000000';
          uiColor = '#000000';
          break;
      case "LIGHT":
          textColor = '#1C1C1E';
          uiColor = '#747474';
          break;
      case "MEDIUM":
          textColor = '#ffffff';
          uiColor = '#F5F5F5';
          break;
      case "DARK":
          textColor = '#ffffff';
          uiColor = '#B8B8B9';
          break;
      default:
          textColor = '#000000';
          uiColor = '#000000';
  }

  const textContrastRatio = colorHex ? calculateColorContrast(textColor, colorHex) : null;
  const textAA = textContrastRatio ? textContrastRatio >= 4.5 : false

  const deepLinkUrl = `${baseDeepLinkDomain}?deeplink=site/${siteId}/mood/${moodId}`
  return (
    <>
      {deleteMoodModalOpen && (
        <MoodDelete
          onCancel={() => setDeleteMoodModalOpen(false)}
          moodRef={moodRef}
          projectId={props.projectId}
          historyPush={historyPush}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit mood journey"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={mood && mood.name && moodSchema.name ? moodSchema.name.read(mood.name) as string : '' }
        toolbar={() => (
          <ButtonGroup>
            {mood &&
              <ReleaseControlUpdate
                hasBorder
                entityRef={moodRef}
                updateHandler={releaseControlUpdate}
              />
            }
            <PopOverMenu hasBorder={true}>
              <PopOverMenuButton onClick={() => setDeleteMoodModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        subnav={() => (
          <SecondaryNavigation
            navItems={getMoodSubnav(projectId, moodId)}
            modified={modified}
            historyPush={historyPush}
          />
        )}
        summary={statusSummary(failures)}
        content={() => (
          <StatusGuard status={moodUpdate} mode="form">
            <datalist id="getty-colors">
              <option value="#addbd3" />
              <option value="#f5ce3e" />
              <option value="#e0592a" />
              <option value="#b32572" />
              <option value="#002e6d" />
              <option value="#009877" />
            </datalist>
            {formBag && mood && (
              <form noValidate onSubmit={(event) => { event.preventDefault() }}>
                <FormData
                  bag={formBag}
                  onUpdate={(event) => {
                    setFormBag(event.bag)
                  }}
                  validate={() => ({})}
                >
                  <Input
                    label="Title"
                    field="name"
                    error={formBag.touched.name ? formBag.errors.name : undefined}
                    warning={getValidationWarning<Mood.Entity>('name', failureFields)}
                  />
                  <Input
                    label="Key"
                    description="Key for this mood"
                    field="key"
                    warning={getValidationWarning<Mood.Entity>('key', failureFields)}
                  />
                  <Input
                    component="textarea"
                    label="Description"
                    field="description"
                    warning={getValidationWarning<Mood.Entity>('description', failureFields)}
                  />
                  <Input
                    component="input"
                    type="color"
                    label="Color"
                    field="color"
                    list="getty-colors"
                    onInput={(event) => { setColorHex(event.target.value) }}
                  />
                  {colorStyleOption("LIGHT", "Black Text, Dark Gray UI Elements")}
                  {colorStyleOption("MEDIUM", "White Text, White UI Elements")}
                  {colorStyleOption("DARK", "White Text, Light Gray UI Elements")}

                  { colorHex && textColor && uiColor &&
                    <>
                      <MobileMockup backgroundColor={colorHex} textColor={textColor} uiColor={uiColor} />
                      {textContrastRatio &&
                        <>
                          <p><a href="https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html">Text Contrast Ratio</a>: {textContrastRatio.toFixed(2)}:1</p>
                          <p><a href="https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html">WCAG AA Compliance</a> (greater than 4.5:1): {textAA ? <IconTick size="base" color="success" /> : <IconCancel size="base" color="error" />}</p>
                        </>
                      }
                    </>
                  }

                  <FieldSection />
                  <MediaField
                    kind="MEDIA_KIND_AUDIO"
                    mediaRef={audioMedia}
                    onUpdate={(entity) => { setAudioMedia(entity ? entity.ref : undefined) }}
                    label="Audio"
                    warning={getValidationWarning<Mood.Entity>('audio', failureFields)}
                  />
                  <FieldSection />
                  <MediaField
                    kind="MEDIA_KIND_RASTER_IMAGE"
                    mediaRef={imageMedia}
                    onUpdate={(entity) => { setImageMedia(entity ? entity.ref : undefined) }}
                    label="Tile Image"
                    warning={getValidationWarning<Mood.Entity>('imageRef', failureFields)}
                  />
                  <Input
                    label="Alternate Text"
                    description="Description of what the image contains if it cannot be shown."
                    field="imageAltText"
                    isAssociated={true}
                    warning={getValidationWarning<Mood.Entity>('imageAltText', failureFields)}
                  />
                  <Input
                    label="Credit"
                    description="Credit the author of the photo or object."
                    field="creditText"
                    isAssociated={true}
                    warning={getValidationWarning<Mood.Entity>('creditText', failureFields)}
                  />
                </FormData>
              </form>
            )}
            <>
              <h4>Deep Link</h4>
              <a href={deepLinkUrl} target="_blank" rel="noopener noreferrer">
                {deepLinkUrl}
              </a>
            </>
          </StatusGuard>
        )}
        sidebar={() => null}
        footer={() => formBag && mood && (
          <FooterEditEntity
            entity={moodUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBag, mood);
              return false;
            }}
          />
        )}
      />
    </>
  )
}

export const MoodUpdateDetails = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => ({
    ...pick(store.getty, 'moodUpdate', 'validationFailuresList'),
  }),
  {
    ...pick(actionCreators, 'gettyGetMoodRequest', 'gettyUpdateMoodRequest', 'gettyUpdateMoodReset', 'gettyListValidationFailuresRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(MoodUpdateDetailsView);
